import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaSave, FaCar, FaUser, FaUndo } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import ConfirmModal from '../../common/ConfirmModal';
import { rootUrl } from '../../../config/config';

const API = `${rootUrl}/api/document-types`;

const TABS = [
    { key: 'car', label: 'Car Documents', Icon: FaCar },
    { key: 'driver', label: 'Driver Documents', Icon: FaUser },
];

const emptyForm = { code: '', name: '', has_expiry: false, is_required: false, sort_order: 0, active: true };

const Flag = ({ value }) => value
    ? <FaCheck className="text-green-500 mx-auto" />
    : <FaTimes className="text-gray-300 mx-auto" />;

const inputCls = 'border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-1 focus:ring-orange-400';
const checkboxCls = 'w-4 h-4 accent-orange-500';

export default function DocumentTypesAdmin() {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${authToken}` };

    const [activeTab, setActiveTab] = useState('car');
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState(emptyForm);
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [deactivateError, setDeactivateError] = useState('');
    const [deactivating, setDeactivating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(API, { headers, params: { entity_type: activeTab } });
            setTypes(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load document types');
        } finally {
            setLoading(false);
        }
    }, [authToken, activeTab]); // eslint-disable-line

    useEffect(() => { load(); }, [load]);

    const switchTab = (key) => {
        setActiveTab(key);
        setEditingId(null);
        setShowAdd(false);
    };

    const startEdit = (t) => {
        setEditingId(t.id);
        setEditForm({ name: t.name, has_expiry: t.has_expiry, is_required: t.is_required, sort_order: t.sort_order, active: t.active });
        setEditError('');
    };

    const cancelEdit = () => { setEditingId(null); setEditForm({}); setEditError(''); };

    const saveEdit = async () => {
        if (!editForm.name.trim()) { setEditError('Name is required'); return; }
        setSaving(true);
        setEditError('');
        try {
            await axios.put(`${API}/${editingId}`, editForm, { headers });
            cancelEdit();
            load();
        } catch (err) {
            setEditError(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        if (!addForm.code.trim() || !addForm.name.trim()) { setAddError('Code and Name are required'); return; }
        if (!/^[A-Z0-9_]+$/.test(addForm.code)) { setAddError('Code can only contain letters, numbers and underscores'); return; }
        setAdding(true);
        setAddError('');
        try {
            await axios.post(API, { ...addForm, entity_type: activeTab }, { headers });
            setShowAdd(false);
            setAddForm(emptyForm);
            load();
        } catch (err) {
            setAddError(err.response?.data?.message || 'Add failed');
        } finally {
            setAdding(false);
        }
    };

    const handleDeactivate = async () => {
        setDeactivating(true);
        setDeactivateError('');
        try {
            await axios.delete(`${API}/${deactivateTarget.id}`, { headers });
            setDeactivateTarget(null);
            load();
        } catch (err) {
            setDeactivateError(err.response?.data?.message || 'Deactivate failed');
        } finally {
            setDeactivating(false);
        }
    };

    const handleReactivate = async (t) => {
        setError('');
        try {
            await axios.post(`${API}/${t.id}/reactivate`, {}, { headers });
            load();
        } catch (err) {
            setError(err.response?.data?.message || 'Reactivate failed');
        }
    };

    // Rendered as a function call (not a nested component) so inputs keep focus while typing.
    const renderFlagInputs = (form, setForm) => (
        <>
            <td className="px-3 py-2 text-center"><input type="checkbox" aria-label="Has expiry" checked={form.has_expiry} onChange={e => setForm(f => ({ ...f, has_expiry: e.target.checked }))} className={checkboxCls} /></td>
            <td className="px-3 py-2 text-center"><input type="checkbox" aria-label="Required" checked={form.is_required} onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))} className={checkboxCls} /></td>
            <td className="px-3 py-2 text-center"><input type="checkbox" aria-label="Active" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className={checkboxCls} /></td>
        </>
    );

    const renderEditRow = (t) => (
        <tr key={t.id} className="bg-orange-50">
            <td className="px-3 py-2"><input type="number" aria-label="Order" value={editForm.sort_order} onChange={e => setEditForm(f => ({ ...f, sort_order: Number(e.target.value) }))} className={`${inputCls} w-16 text-right`} /></td>
            <td className="px-3 py-2 font-mono font-semibold text-[#2a2e30]">{t.code}</td>
            <td className="px-3 py-2"><input aria-label="Name" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className={inputCls} maxLength={100} /></td>
            {renderFlagInputs(editForm, setEditForm)}
            <td className="px-3 py-2 text-right text-gray-500">{t.file_count}</td>
            <td className="px-3 py-2">
                {editError && <p className="text-red-500 text-xs mb-1">{editError}</p>}
                <div className="flex gap-2">
                    <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1 px-3 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 disabled:opacity-50"><FaSave /> {saving ? 'Saving…' : 'Save'}</button>
                    <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"><FaTimes /> Cancel</button>
                </div>
            </td>
        </tr>
    );

    const renderRow = (t) => (
        <tr key={t.id} className={`hover:bg-gray-50 ${!t.active ? 'opacity-50' : ''}`}>
            <td className="px-3 py-3 text-right text-gray-500">{t.sort_order}</td>
            <td className="px-3 py-3 font-mono font-semibold text-[#2a2e30]">{t.code}</td>
            <td className="px-3 py-3 text-[#2a2e30]">{t.name}</td>
            <td className="px-3 py-3 text-center"><Flag value={t.has_expiry} /></td>
            <td className="px-3 py-3 text-center"><Flag value={t.is_required} /></td>
            <td className="px-3 py-3 text-center"><Flag value={t.active} /></td>
            <td className="px-3 py-3 text-right text-gray-500">{t.file_count}</td>
            <td className="px-3 py-3">
                <div className="flex gap-2">
                    <button onClick={() => startEdit(t)} className="p-1.5 text-orange-500 hover:text-orange-700" title="Edit" aria-label={`Edit ${t.name}`}><FaEdit /></button>
                    {t.active ? (
                        <button onClick={() => { setDeactivateTarget(t); setDeactivateError(''); }} className="p-1.5 text-red-400 hover:text-red-600" title="Deactivate" aria-label={`Deactivate ${t.name}`}><FaTrash /></button>
                    ) : (
                        <button onClick={() => handleReactivate(t)} className="p-1.5 text-green-600 hover:text-green-700" title="Reactivate" aria-label={`Reactivate ${t.name}`}><FaUndo /></button>
                    )}
                </div>
            </td>
        </tr>
    );

    const tabLabel = TABS.find(t => t.key === activeTab)?.label;

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[#2a2e30]">Document Types</h1>
                    <p className="text-sm text-gray-500 mt-1">Types offered when uploading car and driver documents, and tracked on the document checklist</p>
                </div>
                <button onClick={() => { setShowAdd(true); setAddError(''); setAddForm(emptyForm); }} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">
                    <FaPlus /> <span className="hidden md:inline">Add Type</span>
                </button>
            </div>

            <div className="flex border-b border-gray-200 mb-6">
                {TABS.map(({ key, label, Icon }) => (
                    <button
                        key={key}
                        onClick={() => switchTab(key)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === key ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <span className="md:hidden"><Icon /></span>
                        <span className="hidden md:inline">{label}</span>
                    </button>
                ))}
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-500 rounded-lg text-sm">{error}</div>}

            <div className="bg-white rounded-2xl shadow-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Order</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Code</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Has Expiry</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Required</th>
                            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Active</th>
                            <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Files</th>
                            <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading && (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
                        )}
                        {!loading && types.length === 0 && !showAdd && (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No document types yet</td></tr>
                        )}
                        {!loading && types.map(t => (editingId === t.id ? renderEditRow(t) : renderRow(t)))}
                        {showAdd && (
                            <tr className="bg-green-50">
                                <td className="px-3 py-2"><input type="number" aria-label="Order" value={addForm.sort_order} onChange={e => setAddForm(f => ({ ...f, sort_order: Number(e.target.value) }))} className={`${inputCls} w-16 text-right`} /></td>
                                <td className="px-3 py-2"><input aria-label="Code" value={addForm.code} onChange={e => setAddForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))} className={inputCls} maxLength={50} placeholder="CODE" /></td>
                                <td className="px-3 py-2"><input aria-label="Name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} className={inputCls} maxLength={100} placeholder="Document name" /></td>
                                {renderFlagInputs(addForm, setAddForm)}
                                <td className="px-3 py-2" />
                                <td className="px-3 py-2">
                                    {addError && <p className="text-red-500 text-xs mb-1">{addError}</p>}
                                    <div className="flex gap-2">
                                        <button onClick={handleAdd} disabled={adding} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"><FaSave /> {adding ? 'Adding…' : 'Add'}</button>
                                        <button onClick={() => setShowAdd(false)} className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"><FaTimes /> Cancel</button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">
                Adding to {tabLabel}. Codes cannot be changed after creation. Deactivated types stay on files already tagged with them but are no longer offered or checked.
            </p>

            <ConfirmModal
                isOpen={Boolean(deactivateTarget)}
                title="Deactivate Document Type"
                description={deactivateTarget
                    ? `Deactivate "${deactivateTarget.name}"? It will no longer be offered for uploads or shown on the checklist.${deactivateTarget.file_count ? ` ${deactivateTarget.file_count} file(s) already tagged with it keep their tag.` : ''} You can reactivate it later.`
                    : ''}
                confirmText="Deactivate"
                cancelText="Cancel"
                variant="danger"
                loading={deactivating}
                error={deactivateError}
                onCancel={() => { setDeactivateTarget(null); setDeactivateError(''); }}
                onConfirm={handleDeactivate}
            />
        </div>
    );
}
